import { useEffect, useRef, useState } from 'react';

import { MAX_WINDOW, PAGE_SIZE } from '../../shared/queries.ts';
import type { IssueFilter } from '../../shared/queries.ts';
import { useLive } from '../live.ts';
import type { IssueCard } from '../types.ts';
import { IssueRow } from './IssueRow.tsx';

/**
 * The list: ONE live window, newest first, whose limit grows a page at a time as you scroll.
 *
 * Growing the limit is a new query (a bigger window), so for a moment the old rows stay up while
 * the new snapshot arrives. A new issue enters at the top of whatever window is open, and a deleted
 * one leaves a gap the window backfills — r2db keeps the window a window.
 */
export function IssueList({ project, filter, selectedId, onOpen, onSearchTag }: { project: string; filter: IssueFilter; selectedId: string | null; onOpen: (id: string) => void; onSearchTag: (name: string) => void }) {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const filterKey = JSON.stringify(filter);
  // The project resets the window as a filter does: a different project is a different list, and
  // carrying 500 rows' worth of limit into it would open a window nobody asked for.
  useEffect(() => setLimit(PAGE_SIZE), [filterKey, project]);
  const live = useLive<IssueCard[]>('issues', { project, limit, filter });
  const rows = live.data ?? [];
  const settled = !live.stale && live.status === 'live';
  const canGrow = settled && rows.length >= limit && limit < MAX_WINDOW;

  const sentinel = useRef<HTMLDivElement>(null);
  const grow = useRef(() => {});
  grow.current = () => { if (canGrow) setLimit(l => Math.min(l + PAGE_SIZE, MAX_WINDOW)); };
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => { if (entries[0]?.isIntersecting) grow.current(); }, { rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // A window that fits on screen never scrolls the sentinel in, so check again once it settles.
  useEffect(() => { if (canGrow && sentinel.current && sentinel.current.getBoundingClientRect().top < window.innerHeight + 600) grow.current(); }, [canGrow, rows.length]);

  return (
    <section className="pane list-pane" aria-label="Issues">
      <header className="pane-head">
        <span className="eyebrow">ISSUES {filter.length ? '/ FILTERED' : '/ NEWEST FIRST'}</span>
        <span className="micro muted">{live.status === 'live' ? `${rows.length}${rows.length >= limit ? '+' : ''} IN WINDOW` : live.status.toUpperCase()}</span>
      </header>
      {live.error && <p className="empty">Could not load issues: {live.error.message}</p>}
      <ul className="issue-list">
        {rows.map(issue => <IssueRow key={issue.id} issue={issue} selected={issue.id === selectedId} onOpen={onOpen} onSearchTag={onSearchTag} />)}
      </ul>
      {!rows.length && !live.error && <p className="empty">{settled ? 'No issues match.' : 'Opening a live query…'}</p>}
      <div ref={sentinel} aria-hidden="true" />
      {canGrow && <button type="button" className="more" onClick={() => grow.current()}>Load {PAGE_SIZE} more</button>}
      {settled && rows.length > 0 && !canGrow && <p className="end micro">{limit >= MAX_WINDOW && rows.length >= limit ? `THE WINDOW STOPS AT ${MAX_WINDOW}. SEARCH TO NARROW IT.` : 'END OF THE LIST'}</p>}
    </section>
  );
}
